
import { motion } from "framer-motion";
import { MessageSquareText, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Subforum {
  id: string;
  name: string;
  description: string;
  member_count: number;
}

interface Thread {
  id: string;
  title: string;
  content: string;
  created_at: string;
  upvotes: number;
  downvotes: number;
  subforum_id: string;
  profiles: {
    username: string;
    avatar_url: string | null;
  };
}

const Forums = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubforum, setSelectedSubforum] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubforumDialogOpen, setIsSubforumDialogOpen] = useState(false);
  const [newThread, setNewThread] = useState({
    title: "",
    content: "",
    subforumId: "",
  });
  const [newSubforum, setNewSubforum] = useState({
    name: "",
    description: "",
  });

  const { data: subforums, isLoading: isLoadingSubforums } = useQuery({
    queryKey: ["subforums"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subforums")
        .select("*")
        .order("member_count", { ascending: false });

      if (error) throw error;
      return data as Subforum[];
    },
  });

  const { data: threads, isLoading: isLoadingThreads } = useQuery({
    queryKey: ["threads", selectedSubforum, searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("threads")
        .select(`
          *,
          profiles (
            username,
            avatar_url
          )
        `)
        .order("created_at", { ascending: false });

      if (selectedSubforum) {
        query = query.eq("subforum_id", selectedSubforum);
      }

      if (searchQuery) {
        query = query.ilike("title", `%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Thread[];
    },
  });

  const handleCreateSubforum = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create a subforum",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from("subforums").insert([
        {
          name: newSubforum.name,
          description: newSubforum.description,
          member_count: 1, // Creator is first member
        },
      ]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Subforum created successfully",
      });
      
      setNewSubforum({ name: "", description: "" });
      setIsSubforumDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create subforum",
        variant: "destructive",
      });
    }
  };

  const handleCreateThread = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create a thread",
        variant: "destructive",
      });
      return;
    }

    if (!newThread.subforumId) {
      toast({
        title: "Subforum required",
        description: "Please select a subforum for your thread",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from("threads").insert([
        {
          title: newThread.title,
          content: newThread.content,
          user_id: user.id,
          subforum_id: newThread.subforumId,
        },
      ]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Thread created successfully",
      });
      
      setNewThread({ title: "", content: "", subforumId: "" });
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create thread",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="container mx-auto py-8 px-4"
    >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl font-bold">
          <span className="text-white">Community</span>{" "}
          <span className="text-flux-cyan">f/</span>
        </h1>
        
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search threads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/20 border-flux-cyan/20"
            />
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1EAEDB] hover:bg-[#1EAEDB]/80 text-black">
                <Plus className="h-4 w-4 mr-2" />
                New Thread
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Thread</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateThread} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subforum">Subforum</Label>
                  <Select
                    value={newThread.subforumId}
                    onValueChange={(value) => 
                      setNewThread(prev => ({ ...prev, subforumId: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a subforum" />
                    </SelectTrigger>
                    <SelectContent>
                      {subforums?.map((subforum) => (
                        <SelectItem key={subforum.id} value={subforum.id}>
                          {subforum.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={newThread.title}
                    onChange={(e) =>
                      setNewThread(prev => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Enter thread title"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    value={newThread.content}
                    onChange={(e) =>
                      setNewThread(prev => ({ ...prev, content: e.target.value }))
                    }
                    placeholder="Write your thread content..."
                    className="min-h-[200px]"
                    required
                  />
                </div>

                <div className="flex justify-end">
                  <Button type="submit" className="bg-[#1EAEDB] hover:bg-[#1EAEDB]/80 text-black">
                    Create Thread
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">f/</h2>
            <Dialog open={isSubforumDialogOpen} onOpenChange={setIsSubforumDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-flux-cyan hover:text-flux-cyan/80"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Subforum</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateSubforum} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={newSubforum.name}
                      onChange={(e) =>
                        setNewSubforum(prev => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Enter subforum name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={newSubforum.description}
                      onChange={(e) =>
                        setNewSubforum(prev => ({ ...prev, description: e.target.value }))
                      }
                      placeholder="Write a brief description..."
                      className="min-h-[100px]"
                      required
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" className="bg-[#1EAEDB] hover:bg-[#1EAEDB]/80 text-black">
                      Create Subforum
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {isLoadingSubforums ? (
            <div className="animate-pulse space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 bg-flux-black/50 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {subforums?.map((subforum) => (
                <Button
                  key={subforum.id}
                  variant="ghost"
                  className={`w-full justify-start ${
                    selectedSubforum === subforum.id
                      ? "bg-flux-cyan/20 text-flux-cyan"
                      : "text-gray-400 hover:text-white"
                  }`}
                  onClick={() => setSelectedSubforum(subforum.id)}
                >
                  <MessageSquareText className="h-4 w-4 mr-2" />
                  {subforum.name}
                  <span className="ml-auto text-xs">
                    {subforum.member_count}
                  </span>
                </Button>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-3">
          {isLoadingThreads ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl bg-flux-black/50 border border-flux-cyan/20 backdrop-blur-xl animate-pulse"
                >
                  <div className="h-6 bg-gray-700/20 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-700/20 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : threads?.length === 0 ? (
            <div className="p-6 rounded-xl bg-flux-black/50 border border-flux-cyan/20 backdrop-blur-xl">
              <div className="text-center text-gray-400">
                <MessageSquareText className="w-12 h-12 mx-auto mb-2 text-flux-cyan" />
                <p>No threads found. Be the first to start a discussion!</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {threads?.map((thread) => (
                <div
                  key={thread.id}
                  className="p-6 rounded-xl bg-flux-black/50 border border-flux-cyan/20 backdrop-blur-xl cursor-pointer hover:bg-flux-black/60 transition-colors"
                  onClick={() => navigate(`/forums/thread/${thread.id}`)}
                >
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {thread.title}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2">
                    {thread.content}
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
                    <span>Posted by @{thread.profiles.username}</span>
                    <span>•</span>
                    <span>
                      {new Date(thread.created_at).toLocaleDateString()}
                    </span>
                    <span>•</span>
                    <span>{thread.upvotes - thread.downvotes} points</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Forums;
